import { AppShell, Avatar, Icon, Logo, Menu } from '@starter/ui';
import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  Link,
  Outlet,
  useRouter,
  useRouterState,
} from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { authClient, useSession } from '../lib/auth.ts';
import { useVideoOffered } from '../video/doors.tsx';

export interface RouterContext {
  queryClient: QueryClient;
}

/**
 * Screens that carry no frame: the whole page is the form. They are also the
 * only screens reachable without a session, which is why the rail can be
 * unconditional everywhere else — the guards on the other routes send a
 * signed-out visitor here before a rail is ever drawn.
 */
const BARE = ['/login', '/forgot-password', '/reset-password', '/verify-email'];

/** Remembered across reloads, because collapsing a rail is a preference. */
const RAIL_KEY = 'starter:rail';

function readRail(): 'expanded' | 'compact' {
  try {
    return localStorage.getItem(RAIL_KEY) === 'compact' ? 'compact' : 'expanded';
  } catch {
    return 'expanded';
  }
}

function Shell({ children }: { children: React.ReactNode }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  /*
   * The column's spacing follows what is **rendered**, not where we are going.
   * `location` becomes the new address the moment a navigation starts, while
   * the old screen is still on it — so a frame reading it loses its padding
   * for a beat and the content visibly jumps into place. The rail's highlight
   * is the opposite case and keeps `location`: moving it on the click is the
   * feedback, not a glitch.
   */
  const rendered = useRouterState({ select: (state) => state.resolvedLocation?.pathname ?? '' });
  const videoOffered = useVideoOffered();
  const [rail, setRail] = useState(readRail);
  const compact = rail === 'compact';

  const toggle = useCallback(() => {
    setRail((was) => {
      const next = was === 'compact' ? 'expanded' : 'compact';
      try {
        localStorage.setItem(RAIL_KEY, next);
      } catch {
        /* a private window is not a reason to refuse to collapse a rail */
      }
      return next;
    });
  }, []);

  /*
   * An index page starts 32 below the top of the column and a detail page 16,
   * because a detail page opens with its breadcrumb bar where an index page
   * has its title. Measured on both; it is the one piece of page spacing the
   * frame owns rather than the page.
   */
  const detail = /^\/(brands|products)\/[^/]+$/.test(rendered);

  /*
   * The home page carries its own column spacing, and it is the only one that
   * does. The original's home is a single element holding both the padding
   * (32/16/64) and the gap (32) between its blocks — so ours has to be one
   * element too, or the measurement is split across two and neither matches.
   */
  const home = rendered === '/';

  return (
    <AppShell.Root rail={rail}>
      <AppShell.Rail data-fidelity="rail" data-testid="app-rail">
        <AppShell.Brand>
          <span className="group-data-[rail=compact]/shell:hidden">
            <Logo height={24} />
          </span>
          <AppShell.RailToggle
            aria-label={compact ? 'Expand the menu' : 'Collapse the menu'}
            data-testid="rail-toggle"
            onClick={toggle}
          >
            <Icon name="panel-left" />
          </AppShell.RailToggle>
        </AppShell.Brand>

        <AppShell.Nav data-fidelity="nav-list">
          <AppShell.NavItem
            data-fidelity="nav-item"
            active={pathname === '/'}
            icon={<Icon name="house" />}
            render={<Link to="/" />}
            title="Home"
          >
            Home
          </AppShell.NavItem>
          <AppShell.NavItem
            data-fidelity="nav-item"
            active={pathname.startsWith('/brands')}
            icon={<Icon name="store" />}
            render={<Link search={{ status: 'active' }} to="/brands" />}
            title="My brands"
          >
            My brands
          </AppShell.NavItem>
          <AppShell.NavItem
            data-fidelity="nav-item"
            active={pathname.startsWith('/products')}
            icon={<Icon name="box" />}
            render={<Link search={{ status: 'active' }} to="/products" />}
            title="My products"
          >
            My products
          </AppShell.NavItem>

          <AppShell.NavItem
            data-fidelity="nav-item"
            active={pathname.startsWith('/image-ads') || pathname.startsWith('/static-ad')}
            icon={<Icon name="image-ads" />}
            render={<Link to="/image-ads" />}
            title="Image ads"
          >
            Image ads
          </AppShell.NavItem>

          {videoOffered ? (
            <AppShell.NavItem
              data-fidelity="nav-item"
              active={pathname.startsWith('/video-ads') || pathname.startsWith('/video-ad')}
              icon={<Icon name="video-ads" />}
              render={<Link to="/video-ads" />}
              title="Video ads"
            >
              Video ads
            </AppShell.NavItem>
          ) : null}

          <AppShell.NavItem
            data-fidelity="nav-item"
            active={pathname.startsWith('/generations')}
            icon={<Icon name="generations" />}
            render={<Link to="/generations" />}
            title="My generations"
          >
            My generations
          </AppShell.NavItem>
        </AppShell.Nav>

        <AppShell.RailSpacer />
        <AccountRow />
      </AppShell.Rail>

      <AppShell.Main data-fidelity="main-column">
        {/*
          The measured 32/16/64 of the home page's column is *this* element's,
          not the page's: the frame owns the column's spacing, which is why the
          index/detail difference lives here too. So the marker the fidelity
          gate reads goes here as well — `data-fidelity` names a measurement,
          and this is the element that holds it.
        */}
        <AppShell.Scroll className={home ? '' : detail ? 'px-4 pt-4 pb-16' : 'px-4 pt-8 pb-16'}>
          {children}
        </AppShell.Scroll>
      </AppShell.Main>
    </AppShell.Root>
  );
}

/**
 * The foot of the rail: who is signed in, and the two things that can be done
 * about it. A menu rather than two rows, because the rail's foot is one row
 * tall in the product and the second thing — signing out — is rare.
 */
function AccountRow() {
  const { data: session } = useSession();
  const router = useRouter();

  if (!session) return null;

  const email = session.user.email;
  const name = session.user.name?.trim() || email;

  return (
    <Menu.Root>
      <Menu.Trigger render={<AppShell.RailRow data-testid="rail-account-button" />}>
        <Avatar fallback={name.slice(0, 1).toUpperCase()} size="sm" />
        <span
          className="flex min-w-0 flex-col group-data-[rail=compact]/shell:hidden"
          data-testid="rail-account-identity"
        >
          <span className="truncate text-bodysm text-ink">{name}</span>
          <span className="truncate text-2xs text-ink-muted" data-testid="session-email">
            {email}
          </span>
        </span>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner align="start" side="top">
          <Menu.Popup>
            <Menu.Item render={<Link search={{}} to="/account" />}>Account</Menu.Item>
            <Menu.Item
              data-testid="sign-out"
              onClick={async () => {
                await authClient.signOut();
                await router.invalidate();
                await router.navigate({ to: '/login', search: {} });
              }}
            >
              Sign out
            </Menu.Item>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

function RootLayout() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // A mockup is a whole screen, not a page inside the app. Wrapping one in this
  // chrome would show it at the app's width, inside the app's frame — exactly
  // the framing a mockup exists to propose an alternative to.
  if (pathname.startsWith('/scratchpad/')) return <Outlet />;

  // The funnel is the same case for a different reason: it runs before anybody
  // has an account, so there is no rail to draw, nobody to put at the foot of
  // it, and nothing the application frame could truthfully say. It brings its
  // own frame, which is a card in a field (docs and prd/measured/funnel.json).
  if (pathname.startsWith('/start')) return <Outlet />;

  if (BARE.some((route) => pathname.startsWith(route))) {
    return (
      <div className="min-h-dvh bg-surface">
        <main className="mx-auto max-w-4xl px-4 py-10">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <Shell>
      <Outlet />
    </Shell>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: () => (
    <div className="text-center">
      <h1 className="font-display text-2xl font-bold tracking-tight text-ink">Not found</h1>
      <p className="mt-2 text-sm text-ink-muted">
        <Link to="/" className="text-brand-800 underline">
          Back home
        </Link>
      </p>
    </div>
  ),
});
